import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Copy, Trash2, FileText, Search } from 'lucide-react';
import { getScriptHistory, deleteHistoryEntry } from '../lib/storage/database';

const History = () => {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const historyData = getScriptHistory(100);
      setHistory(historyData || []);
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteEntry = (id) => {
    if (window.confirm('¿Eliminar esta entrada del historial?')) {
      deleteHistoryEntry(id);
      fetchHistory();
    }
  };

  const downloadScript = (script, index) => {
    const blob = new Blob([script], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `script_${index}_${Date.now()}.rsc`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => navigate('/scripts')}
            className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors"
            data-testid="back-home-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Volver</span>
          </button>
          
          <h1 className="text-3xl font-bold text-white">
            Historial de Scripts
          </h1>
          
          <div className="w-20"></div>
        </div>

        {/* Content */}
        <div className="bg-gray-800 rounded-lg p-6 shadow-xl">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
              <p className="text-gray-400 mt-4">Cargando historial...</p>
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-12">
              <FileCode className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">No hay scripts en el historial</p>
              <button
                onClick={() => navigate('/scripts')}
                className="mt-6 px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
              >
                Crear primer script
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {history.map((item, index) => (
                <div
                  key={item.id || index}
                  className="bg-gray-700 rounded-lg p-4 hover:bg-gray-650 transition-colors"
                  data-testid={`history-item-${index}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-sm font-semibold">
                          {item.tipo_config || 'Balanceador'}
                        </span>
                        {item.config?.tipo && (
                          <span className="text-gray-400 text-sm">
                            {item.config.tipo.toUpperCase()}
                          </span>
                        )}
                        {item.config?.wans && (
                          <span className="text-gray-400 text-sm">
                            {item.config.wans.length} WANs
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2 text-gray-400 text-sm mb-2">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(item.timestamp)}</span>
                      </div>

                      {item.config?.router?.nombre && (
                        <p className="text-gray-300 text-sm">
                          Router: <span className="font-semibold">{item.config.router.nombre}</span>
                        </p>
                      )}
                    </div>

                    <button
                      onClick={() => downloadScript(item.script, index)}
                      data-testid={`download-${index}`}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      Descargar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default History;
